import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { styled, Typography } from "@mui/material";
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import Row from "components/common/Row";
import Column from "components/common/Column";
import { MissionContext } from 'modules/pvs_activities/components/contexts/MissionTasksContext';
import { useSelector } from 'react-redux';
import { USER_ROLES } from 'constants';
import { EXPERTS_POSITIONS, MOVEMENTS_STATUS } from 'modules/pvs_activities/constants';

const MissionProcessSteps = ({ processesData, movements, isUserSpecific, isPeerReviewer }) => {
    const missionContext = useContext(MissionContext);
    const scrollContainerRef = useRef(null);
    const authData = useSelector(s => s.auth.data);

    const [processes, setProcesses] = useState(processesData);

    const handleScrollLeft = () => {
        scrollContainerRef.current.scrollBy({
            left: -300,
            behavior: "smooth",
        });
    };

    const handleScrollRight = () => {
        scrollContainerRef.current.scrollBy({
            left: 300,
            behavior: "smooth",
        });
    };

    const scrollToPosition = () => {
        const currentActive = movements.find(movement => movement.is_enabled);
        if (currentActive && currentActive.id) {
            document.getElementById(`movement-${currentActive?.relationProcess?.id}`)?.scrollIntoView({
                behavior: "smooth",
                inline: "center"
            });
        }
    };

    const checkForProcessStatus = (movementData = []) => {
        const completedCheck = movementData.filter(movement => parseInt(movement.status) === 2);
        const assignedCheck = movementData.filter(movement => movement.status === 1);
        const isFullCompleted = completedCheck.length === movementData.length;
        const skippedCheck = movementData.filter(movement => movement.status === MOVEMENTS_STATUS.skip);
        return {
            isManager: movementData.some(x => x.user_id === missionContext.missionData?.mission_manager_id),
            isFullCompleted,
            isPartialCompleted: completedCheck.length !== movementData.length && completedCheck.length > 0,
            isFullAssigned: assignedCheck.length === movementData.length,
            isPartialAssigned: assignedCheck.length !== movementData.length && assignedCheck.length > 0,
            isDisabled: movementData.filter(movement => !movement.is_enabled && movement.status !== 2).length === movementData.length,
            isSkipped: skippedCheck.length === movementData.length
        };
    }

    const getStatusClass = (processStatus) => {
        if (processStatus.isDisabled) return { text_class: "text-muted", shadow: "gray-gradient-shadow" };
        if (processStatus.isFullCompleted) return { text_class: "text-success", shadow: 'green-gradient-shadow' };
        if (processStatus.isPartialCompleted) return { text_class: "text-warning", shadow: 'yellow-gradient-shadow' };
        if (processStatus.isPartialAssigned) return { text_class: "text-warning", shadow: 'yellow-gradient-shadow' };
        if (processStatus.isSkipped) return { text_class: "text-skipped", shadow: 'soft-yellow-gradient-shadow' };
        if (processStatus.isManager) return { text_class: "text-primary", shadow: 'red-orange-gradient-shadow' };
        if (processStatus.isFullAssigned) return { text_class: "text-completed", shadow: 'coral-gradient-shadow' };
        return { text_class: "text-muted", shadow: "gray-gradient-shadow" };
    };
    const getStatusClassForStepper = (processStatus) => {
        if (processStatus.isDisabled) return "active";
        if (processStatus.isFullCompleted) return "completed";
        if (processStatus.isPartialCompleted) return "partial";
        if (processStatus.isPartialAssigned) return "partial";
        if (processStatus.isSkipped) return "skipped";
        if (processStatus.isManager) return "required-action";
        if (processStatus.isFullAssigned) return "assigned";
        return "active";
    };

    const HtmlTooltip = styled(({ className, ...props }) => (
        <Tooltip {...props} classes={{ popper: className }} placement="top-start" />
    ))(({ theme }) => ({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: '#f5f5f9',
            color: 'rgba(0, 0, 0, 0.87)',
            maxWidth: 1000,
            fontSize: theme.typography.pxToRem(12),
            border: '1px solid #787878',
            padding: '30px',
            boxShadow: '1px 2px 9px #A9A9A9',
        },
    }));

    useEffect(() => {
        setProcesses(isUserSpecific ?
            (isPeerReviewer ?
                processesData.filter(process =>
                    process.sub_processes?.some(
                        subProcess => subProcess.sub_role_id === EXPERTS_POSITIONS.peer_reviewer
                    )
                )
                : processesData.filter(
                    process => process.role_id === authData?.role_id
                )
            )
            : processesData)
    }, [processesData, isUserSpecific]);

    useLayoutEffect(() => {
        setTimeout(() => {
            scrollToPosition();
        }, 1000);
    }, [scrollContainerRef.current]);

    return (
        <div style={{
            display: "flex",
            alignItems: "center",
            padding: processes && processes.length < 5 ? "0 10px" : "0 0",
        }}>
            {
                processes && processes.length > 4 &&
                <button button className="btn-scroll" onClick={handleScrollLeft}>
                    <i className="fas fa-chevron-left"></i>
                </button>
            }

            <div
                id="scroll-container"
                ref={scrollContainerRef}
                style={{ display: "flex", overflowX: "auto", whiteSpace: "nowrap", gap: "5px" }}
            >
                <div className="card-progress border border-secondary bg-white">
                    <div className="stepper-wrapper">
                        {
                            processes.map((process, index) => {
                                const movement = movements.filter((movement) => String(movement.process_id) === String(process.id));
                                const processStatus = checkForProcessStatus(movement);
                                const stepperClass = getStatusClassForStepper(processStatus);
                                return (
                                    <div key={index} className={stepperClass + ' stepper-item'}>
                                        <div className="step-counter">{index + 1}</div>
                                    </div>
                                );
                            })
                        }
                    </div>
                    <div
                        className="d-flex justify-content-evenly pt-4"
                        style={{ display: "flex", whiteSpace: "nowrap", gap: "5px" }}
                    >
                        {
                            processes.map((process, index) => {
                                const movement = movements.filter((movement) => String(movement.process_id) === String(process.id));
                                const processStatus = checkForProcessStatus(movement);
                                const classes = getStatusClass(processStatus);
                                return (
                                    <div
                                        key={index + 1}
                                        className={`card d-flex px-2 ${classes.shadow}`}
                                        style={{ width: "250px", flexShrink: 0 }}
                                        id={`movement-${process.id}`}
                                    >
                                        <div className="card-body" style={{ padding: "0.5rem" }}>
                                            <div className="d-flex">
                                                <HtmlTooltip
                                                    title={
                                                        <React.Fragment>
                                                            <Typography color="inherit">
                                                                <InfoCard movementData={movement} className={classes.text_class} />
                                                            </Typography>
                                                        </React.Fragment>
                                                    }
                                                >
                                                    <i className={`fa-solid fa-circle-info fs-5 ${classes.text_class}`}></i>
                                                </HtmlTooltip>
                                                {/* <p className="mx-2 text-muted">Step {index + 1}</p> */}
                                            </div>
                                            <p className="card-text text-center">
                                                {
                                                    processStatus.isDisabled ?
                                                        <span className="text-muted">Unavailable</span>
                                                        : processStatus.isPartialCompleted
                                                            ? <span className="text-warning">Partial Completed</span>
                                                            : processStatus.isFullCompleted ? (
                                                                <span className="text-success">Completed</span>
                                                            ) : processStatus.isSkipped ? (
                                                                <span className="text-skipped">Skipped</span>
                                                            ) : (
                                                                processStatus.isManager ? (
                                                                    <span className={"text-primary"}>Requiring Action</span>
                                                                ) : processStatus.isFullAssigned ? (
                                                                    <span className="text-completed">Awaiting Completion</span>
                                                                ) : ""
                                                            )
                                                }
                                            </p>
                                            <p className="card-text text-center">
                                                <span className={`${classes.text_class} text-wrap`}>{process?.name}</span>
                                            </p>
                                        </div>
                                    </div>
                                );
                            })
                        }
                    </div>
                </div>
            </div>

            {
                processes && processes.length > 4 &&
                <button className="btn-scroll" onClick={handleScrollRight}>
                    <i className="fas fa-chevron-right"></i>
                </button>
            }

        </div >
    )
}

const InfoCard = ({ movementData, className }) => {

    return (
        <>
            {
                movementData?.map(movement => (
                    <Row>
                        <Column col={12}>
                            <p className={`h5 ${className}`}>{movement?.relationUser?.name}</p>
                            <p className="text-muted m-0">Assigned date: {movement?.assigned_date_formatted}</p>
                            <p className="mb-3 text-muted">Completed date: {movement?.completed_date_formatted}</p>
                            {
                                movement.relationSubMovement && movement.relationSubMovement.length > 0 && (
                                    <div className="ml-4">
                                        <h6 className="text-dark">Submovements:</h6>
                                        {
                                            movement.relationSubMovement?.map(sub => (
                                                <Row key={sub.id} className="mb-2">
                                                    <Column col={12}>
                                                        <p className="text-info m-0">{sub.relationUser.name}</p>
                                                        <p className="text-muted m-0">Assigned date: {sub?.assigned_date_formatted}</p>
                                                        <p className="text-muted">Submission date: {sub?.submission_date_formatted}</p>
                                                    </Column>
                                                </Row>
                                            ))}
                                    </div>
                                )
                            }
                        </Column>
                    </Row>
                ))
            }
        </>
    )
}

export default MissionProcessSteps;